import {
    BaseQueryFn,
    createApi,
    fetchBaseQuery,
    FetchBaseQueryError,
    FetchBaseQueryMeta, QueryReturnValue,
    retry
} from '@reduxjs/toolkit/query/react'
import {setGlobalError} from "./globalSlice/globalSlice";
import {skipToken} from "@reduxjs/toolkit/query";
import {showToast} from "../helpers/utils/Utils";

let errorHandlingOptions: ExtraOptions | null = null;

// Define the extra options interface
export interface ExtraOptions {
    redirectToErrorPage?: boolean;
    skip?: boolean;
}

export const fetchWithExtraOptions = <T>(
    endpoint: keyof typeof tgxApi.endpoints,
    args: T,
    extraOptions: ExtraOptions = {}
) => {
    const skip = errorHandlingOptions?.skip || false;

    if (skip) {
        return skipToken;
    }

    errorHandlingOptions = extraOptions;

    // @ts-ignore
    return endpoint.initiate();
};

const customBaseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_URL,
});

// Define the custom base query with error handling
const baseQueryWithErrorHandling: BaseQueryFn<
    string | { url: string; method?: string; headers?: Record<string, string> },
    unknown,
    FetchBaseQueryError,
    ExtraOptions,
    FetchBaseQueryMeta
> = async (args, api, extraOptions = {}) => {

    // Destructure the options and set default values
    const redirectToErrorPage = errorHandlingOptions?.redirectToErrorPage || false;

    // Make the request using the custom base query
    const result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta> = await customBaseQuery(args, api, extraOptions);

    if (result.error?.status === 307){
        showToast("Request rejected, MFA must be configured at 'POST /api/mfa-devices' or at the Profile page in the web application.", "error");
        // Redirect to the Profile page
        window.location.href = "/profile";  // Adjust the URL as per your route setup
    }

    // Check for errors and handle redirection if needed
    if (result.error && redirectToErrorPage) {
        // Dispatch the global error with the error message and status
        api.dispatch(
            setGlobalError({
                statusCode: result.error.status || 500,
                message: (result.error.data as { message?: string })?.message || "An unexpected error occurred",
            })
        );
    }

    // Reset error handling options after processing the request
    errorHandlingOptions = { redirectToErrorPage: false };

    return result;
};


// Wrap with retry logic and set maxRetries to 1
export const tgxApi = createApi({
    reducerPath: "tgxApi",
    baseQuery: retry(baseQueryWithErrorHandling, {
        maxRetries: 0,
    }),
    refetchOnMountOrArgChange: 15,

    tagTypes: [
        'certificateNotesByReferenceId',
        'baseUrl',
        'tenants',
        'getTenantById',
        'getTenantByUrl',
        'getTenantByName',
        'userSettings',
        'appraisalRolls',
        'getPrimaryCollectorByStateAndCounty',
        'getAllCustomerList',
        'collectors',
        'collectorById',
        'collectorNotes',
        'collectorNotesById',
        'getCollectorConfig',
        'getCollectorConfigById',
        'collectorDocumentsById',
        'collectorLogsById',
        'billReleases',
        'billReleaseById',
        'tenantsList',
        'linkedAccounts',
        'linkedAccountById',
        'highRiskAccountVerified',
        'collectorLinks',
        'collectorLinkById',
        'taxRoundingConfigs',
        'taxRoundingConfigById',
        'taxRolls',
        'taxRollById',
        'notes',
        'noteById',
        'errorLogs',
        'codes',
        'codeById',
        'codesList',
        'importVersions',
        'qcRules',
        'qcRuleById',
        'scraperModules',
        'scraperModulesList',
        'scraperModuleById',
        'tenant',
        'accountTransById',
        'accountTrans',
        'recordAudits',
        'bulkSubdivision',
        'subDivisionsByAssociationByName',
        'subdivisionByNameAndCounty',
        'subdivisionAssociation',
        'addAssociation',
        'getContactByCompany',
        'subDivisions',
        'subDivisionsById',
        'subDivisionsNoteService',
        'subDivisionsNoteServiceById',
        'subDivisionsByAssociation',
        'subDivisionsByManagement',
        'subdivisionDocumentList',
        'subDivisionNotesServiceById',
        'subDivisionById',
        'parentAccount',
        'association',
        'associationById',
        'autoHoa',
        'autoHoaById',
        'managementCompanies',
        'managementCompaniesById',
        'sortHoaFeeType',
        'sortHoaFeeTypeById',
        'thirdPartyProvider',
        'thirdPartyProviderById',
        'recordAuditById',
        'recordAuditNameList',
        'registeredScrapper',
        'stateCode',
        'stateCodeById',
        'jurisdictions',
        'jurisdiction',
        'jurisdictionById',
        'jurisdictionByCollectorId',
        'jurisdictionTaxList',
        'jurisdictionNextMonthDues',
        'reportHistory',
        'reportHistoryById',
        'stateCodeAlias',
        'stateCodeAliasById',
        'orderEvent',
        'orderEventById',
        'childAccount',
        'childAccountById',
        'utilData',
        'orderNoteList',
        'orderDocumentList',
        'productById',
        'requirementsForInstrument',
        'recipientInstruments',
        'systemPropertyById',
        'users',
        'userById',
        'systemProperties',
        'recordingPackageByProductId',
        'cannedNotes',
        'customers',
        'customerById',
        'branches',
        'regions',
        'regionById',
        'branchContacts',
        'pdfSettingsByCustomerId',
        'fetchSimplifileTokenStatus',
        'followUpTypeById',
        'followUpTypes',
        'products',
        'findAllRoles',
        'roleById',
        'states',
        'stateById',
        'cannedNoteById',
        'counties',
        'countyById',
        'teams',
        'teamById',
        'teamLeaders',
        'teamMembers',
        'teamRules',
        'teamRuleById',               
        'metroAreas',
        'cannedFileNames',
        'cannedFileNameById',
        'metroAreaById',
        'platformPricing',
        'revenueEvents',
        'platformPricingById',
        'tenantPricing',
        'tenantPricingById',
        'partnerPricing',
        'partnerPricingById',
        'vendorPricing',
        'vendorPricingById',
        'assignmentLogs',
        'invoiceLines',
        'invoiceLineById',
        'productPricing',
        'branchById',
        'invoiceLines',
        'invoiceNotes',
        'deliveryModalDocuments',
        'orderLogs',
        'userAuditableInfo',
        'roles',
        'defaultInvoiceLines',
        'teamAndUserAssignments',
        'mfaDevices',
        'countiesByStateIds',
        'report',
        'reportById',
        'getAllFulfillmentCustomerByTenant',
        'highRiskAccount',
        'integrations',
        'reswareCredentials',
        'reswareTypeMappings',
        'orderListByCustomerId',
        'orderById',
        'getOrderResultByOrderId',
        'getAllFileDataNew',
        'getAllFileDataOld',
        'getOrderByOrderIdAndCustomerId',
        'orders',
        'getContactEmailByOrderId',
        'checkPrimaryCollectorExist',
        'getOrderByOrderIdAndType',
        'getHoaOrderResultByOrderId',
        'productTaskList',
        'qcLogicResultList',
        'ProductQcLogicResult',
        'productDocumentList',
        "orderToDoList",
        "userProfileInfo",
        "associationFeeById",
        "contactsById",
        "associationsSubdivision",
        "getMyOrders",
        "associationsDocuments",
        "certificateOptionsByOrderId",
        "managementCompanyNotesById",
        "certificateNotesById",
        "contactById",
        "managementCompanySubdivision",
        "customerPricing",
        "customerPricingById",
        "contactByBranchId",
        "certificateOptionsByOrderId",
        "fulfillmentPricing",
        "fulfillmentPricingById",
        "fnfTransaction",
        "payments",
        "certificateOptionsByOrderId",
        "subdivisionWithComputedNameById",
        "getAllCountyByStateAbbr",
        "subdivisionWithComputedNameById",
        "nationalCertificateOptionsByOrderId",
        "nationalOrderLogs",
        "nationalOrderResultByOrderId",
        "thirdPartyProviderCertificateNotesById",
        "thirdPartyProviderNotesById",
        "certificateNotesByReferenceId",
        "getAllDocuments",
        "getAllFilesByRelationId",
        "getPreviewOrderList",
        "reswareTypeMappingsById",
        "reswareTypeCredentialsById",
        'validateTaxGeneratePdf',
        'pdfImageByName',
        'associationAssignmentLogs',
        'taxSuitsCollector',
        'subDivisionChildAccountNotes'
    ],
    endpoints: () => ({}),
})